import axios from 'axios'
import type {
  CaseImageVariantStatusResponse,
  ProcessCaseImageVariantResponse,
} from '@/api/caseImageVariants'
import {
  CASE_IMAGE_JOB_STATUS_KEY,
  CASE_IMAGE_VARIANT_ERROR_CODE,
  CASE_IMAGE_VARIANT_KIND_KEY,
  CASE_IMAGE_VARIANT_POLL_INTERVAL_MS,
} from '@messenger/consts'
import {
  isTransientPollError,
  MAX_CONSECUTIVE_POLL_ERRORS,
} from '@/api/caseImageJobPolling'
import { ENHANCE_TIMEOUT_MS } from './enhanceConstants'
import { isAiImageEditsDisabledError } from './variantErrorCodes'

/**
 * Why a generation did not produce a result. The UI maps each reason to copy
 * and decides whether "Try again" makes sense.
 *
 * - `failed`: the backend marked the variant FAILED.
 * - `timeout`: the poll budget ran out with no terminal status.
 * - `rate-limited`: the process call answered 429 (daily cap reached); the
 *   message carries the backend's own explanation.
 * - `disabled`: the process call answered 403 `AI_IMAGE_EDITS_DISABLED`; AI
 *   image edits are switched off and nothing was started.
 * - `refused`: the process call answered 422 with a code (the photo is too
 *   wide or tall for the model); nothing was started.
 * - `cancelled`: the caller stopped the run (user pressed Cancel or left).
 * - `request`: any other request failure (network, auth, 5xx after retries).
 */
export type EnhanceGenerationFailureReason =
  | 'failed'
  | 'timeout'
  | 'rate-limited'
  | 'disabled'
  | 'refused'
  | 'cancelled'
  | 'request'

/** Thrown by `runEnhanceGeneration` for every non-success outcome. */
export class EnhanceGenerationError extends Error {
  reason: EnhanceGenerationFailureReason
  /**
   * The backend's machine-readable failure code (`errorCode` on a FAILED
   * status, `code` in a 429 or a disabled 403 body), or null when the
   * backend sent none. The
   * UI maps it to copy and to whether "Try again" is offered.
   */
  code: string | null

  constructor(
    reason: EnhanceGenerationFailureReason,
    message: string,
    code: string | null = null,
  ) {
    super(message)
    this.name = 'EnhanceGenerationError'
    this.reason = reason
    this.code = code
  }
}

/** A finished generation the user can now compare and accept. */
export type EnhanceGenerationResult = {
  variantId: string
  url: string
}

/**
 * Everything the runner touches from the outside, so tests can drive it with
 * fakes and fake timers instead of a network and a real clock.
 */
export type EnhanceGenerationDeps = {
  process: (caseImageId: string) => Promise<ProcessCaseImageVariantResponse>
  getStatus: (variantId: string) => Promise<CaseImageVariantStatusResponse>
  /** Reports each polled status so the UI can show queue vs processing. */
  onStatus?: (status: CaseImageVariantStatusResponse['status']) => void
  /** Checked before every wait and poll; true stops the run. */
  isCancelled?: () => boolean
  sleep?: (ms: number) => Promise<void>
  now?: () => number
  pollIntervalMs?: number
  timeoutMs?: number
}

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms))

/**
 * The `code` and `message` a 429 carries in its JSON body. Null when the
 * error is not a 429. A 429 without a code still means the daily cap, so
 * that code is assumed.
 */
const rateLimitBody = (
  err: unknown,
): { code: string; message: string } | null => {
  if (!axios.isAxiosError(err) || err.response?.status !== 429) return null
  const body = err.response.data as
    | { code?: unknown; message?: unknown }
    | undefined
  return {
    code:
      typeof body?.code === 'string' && body.code.length > 0
        ? body.code
        : CASE_IMAGE_VARIANT_ERROR_CODE.DAILY_CAP_REACHED,
    message: typeof body?.message === 'string' ? body.message : '',
  }
}

/**
 * The `code` a 422 from the process call carries, or null when the error is
 * not a 422 with one.
 */
const unprocessableCode = (err: unknown): string | null => {
  if (!axios.isAxiosError(err) || err.response?.status !== 422) return null
  const body = err.response.data as { code?: unknown } | undefined
  return typeof body?.code === 'string' && body.code.length > 0
    ? body.code
    : null
}

/**
 * Run one upscale generation end to end: ask the backend to start it, then
 * poll its status until it completes, fails, is cancelled, or the time budget
 * runs out. Accepting is deliberately not part of this: it is billable and
 * must come from a separate user action.
 */
export const runEnhanceGeneration = async (
  caseImageId: string,
  deps: EnhanceGenerationDeps,
): Promise<EnhanceGenerationResult> => {
  const {
    process,
    getStatus,
    onStatus,
    isCancelled = () => false,
    sleep = defaultSleep,
    now = Date.now,
    pollIntervalMs = CASE_IMAGE_VARIANT_POLL_INTERVAL_MS,
    timeoutMs = ENHANCE_TIMEOUT_MS,
  } = deps

  const cancelledError = () =>
    new EnhanceGenerationError('cancelled', 'Enhancement cancelled')

  let started: ProcessCaseImageVariantResponse
  try {
    started = await process(caseImageId)
  } catch (err) {
    const refusedCode = unprocessableCode(err)
    if (refusedCode) {
      throw new EnhanceGenerationError(
        'refused',
        'The photo cannot be enhanced',
        refusedCode,
      )
    }
    const limit = rateLimitBody(err)
    if (limit) {
      throw new EnhanceGenerationError(
        'rate-limited',
        limit.message,
        limit.code,
      )
    }
    if (isAiImageEditsDisabledError(err)) {
      throw new EnhanceGenerationError(
        'disabled',
        'AI image edits are switched off',
        CASE_IMAGE_VARIANT_ERROR_CODE.AI_IMAGE_EDITS_DISABLED,
      )
    }
    throw new EnhanceGenerationError(
      'request',
      err instanceof Error ? err.message : 'Failed to start enhancement',
    )
  }
  if (isCancelled()) throw cancelledError()
  onStatus?.(started.status)

  const deadline = now() + timeoutMs
  let consecutiveErrors = 0

  while (now() < deadline) {
    let status: CaseImageVariantStatusResponse
    try {
      status = await getStatus(started.variantId)
    } catch (err) {
      // A brief network blip or a 5xx must not fail a generation that is
      // still running fine on the worker; only a sustained outage does.
      if (
        !isTransientPollError(err) ||
        ++consecutiveErrors >= MAX_CONSECUTIVE_POLL_ERRORS
      ) {
        throw new EnhanceGenerationError(
          'request',
          err instanceof Error ? err.message : 'Failed to check enhancement',
        )
      }
      if (isCancelled()) throw cancelledError()
      await sleep(pollIntervalMs)
      continue
    }
    consecutiveErrors = 0
    if (isCancelled()) throw cancelledError()
    onStatus?.(status.status)

    if (status.status === CASE_IMAGE_JOB_STATUS_KEY.COMPLETED) {
      if (!status.url) {
        throw new EnhanceGenerationError(
          'failed',
          'Enhancement finished without an image',
        )
      }
      return { variantId: status.variantId, url: status.url }
    }
    if (status.status === CASE_IMAGE_JOB_STATUS_KEY.FAILED) {
      throw new EnhanceGenerationError(
        'failed',
        'Enhancement failed',
        status.errorCode ?? null,
      )
    }

    await sleep(pollIntervalMs)
    if (isCancelled()) throw cancelledError()
  }

  throw new EnhanceGenerationError('timeout', 'Enhancement timed out')
}

/** The kind every grid "Enhance" action requests. */
export const ENHANCE_VARIANT_KIND = CASE_IMAGE_VARIANT_KIND_KEY.UPSCALE
